import Button from '@amzn/awsui-components-core/button';
import DateInput from '@amzn/awsui-components-core/date-input';
import FormField from '@amzn/awsui-components-core/form-field';
import Icon from '@amzn/awsui-components-core/icon';
import React from 'react';
import { ReportVersionRangeCalendar } from 'src/components/star-grid/components/ReportVersionRangeCalendar';
import { versionRangeTitle } from 'src/components/star-grid/utils/versionPickerPresentation';
import { useVersionDropdown } from 'src/components/star-grid/hooks/useVersionDropdown';
import { VERSION_DATE_PRESETS } from 'src/components/star-grid/versionDateFilterConstants';
import { snapshotDateRangeLabel } from 'src/components/star-grid/utils/versionDateFilter';
import styles from 'src/components/star-grid/components/styles/ReportVersionSelector.module.scss';

type State = ReturnType<typeof useVersionDropdown>;

const VersionDateInputs: React.FC<{ readonly state: State }> = ({ state: { dates, text } }) => (
  <div className={styles.dateInputs}>
    <FormField label={text('versionStartDate')}>
      <div className={styles.dateInputShell}>
        <DateInput
          invalid={dates.hasError}
          value={dates.draft.startDate}
          onChange={({ detail }) => dates.setDate('startDate', detail.value)}
          onFocus={() => dates.setActiveField('startDate')}
          placeholder={text('versionDateFormat')}
          ariaLabel={text('versionStartDate')}
          data-testid="version-start-date"
        />
        <span className={styles.dateInputIcon} aria-hidden="true">
          <Icon name="calendar" size="small" />
        </span>
      </div>
    </FormField>
    <FormField label={text('versionEndDate')}>
      <div className={styles.dateInputShell}>
        <DateInput
          invalid={dates.hasError}
          value={dates.draft.endDate}
          onChange={({ detail }) => dates.setDate('endDate', detail.value)}
          onFocus={() => dates.setActiveField('endDate')}
          placeholder={text('versionDateFormat')}
          ariaLabel={text('versionEndDate')}
          data-testid="version-end-date"
        />
        <span className={styles.dateInputIcon} aria-hidden="true">
          <Icon name="calendar" size="small" />
        </span>
      </div>
    </FormField>
  </div>
);

const VersionDateEditor: React.FC<{ readonly state: State }> = ({ state }) => (
  <div className={styles.dateEditor} data-testid="version-date-editor" role="group" aria-label={state.text('versionDateRange')}>
    <div className={styles.presets} role="group" aria-label={state.text('versionDateRange')}>
      {VERSION_DATE_PRESETS.map((preset) => (
        <Button
          key={preset.id}
          formAction="none"
          onClick={() => state.dates.setPreset(preset.id)}
          data-testid={`version-preset-${preset.id}`}
          nativeButtonAttributes={{ 'aria-pressed': state.dates.range.preset === preset.id }}
        >
          {state.text(preset.labelKey)}
        </Button>
      ))}
    </div>
    <VersionDateInputs state={state} />
    <ReportVersionRangeCalendar state={state} />
    {state.dates.hasError && (
      <div className={styles.dateError} role="alert">
        {state.text('versionInvalidDates')}
      </div>
    )}
  </div>
);

export const ReportVersionDateFilter: React.FC<{ readonly state: State }> = ({ state }) => (
  <>
    <div className={styles.rangeControl}>
      <Button
        ref={state.dates.triggerRef}
        formAction="none"
        onClick={state.dates.editing ? state.dates.dismiss : state.dates.open}
        nativeButtonAttributes={{ 'aria-expanded': state.dates.editing }}
        data-testid="version-date-range"
      >
        <span className={styles.rangeTriggerContent}>
          <Icon name="calendar" size="small" />
          <span className={styles.rangeTitle}>{versionRangeTitle(state.dates.range, state.text)}</span>
          <span className={styles.rangeValue}>{snapshotDateRangeLabel(state.dates.range)}</span>
          <Icon name="angle-down" size="small" />
        </span>
      </Button>
      {state.dates.range.preset !== 'all' && (
        <Button variant="inline-link" formAction="none" onClick={state.dates.clear} data-testid="version-clear-dates">
          {state.text('versionClearDates')}
        </Button>
      )}
    </div>
    <p className={styles.scopeNote}>{state.text('versionDateScope')}</p>
    {state.dates.editing && <VersionDateEditor state={state} />}
  </>
);
